/**
 * ColorSchemeProvider — Global accent color picker.
 *
 * Light mode only. User picks a color scheme (blue, green, purple, rose, plain)
 * which sets CSS variables on <html> via a class like `theme-blue`, or a
 * custom colour (meeting 2026-10-22, item 27) whose variables are set inline.
 *
 * Stored in localStorage as `color-scheme` (+ `color-scheme-custom`, the hex).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ColorPickerPopover } from '@/components/ui/ColorPickerPopover';
import { customSchemeVars, parseHex } from '@/utils/color';

export type ColorScheme = 'blue' | 'green' | 'purple' | 'rose' | 'plain' | 'custom';

type PresetScheme = Exclude<ColorScheme, 'custom'>;

const COLOR_SCHEMES: PresetScheme[] = ['blue', 'green', 'purple', 'rose', 'plain'];

const STORAGE_KEY = 'color-scheme';
const CUSTOM_STORAGE_KEY = 'color-scheme-custom';
const CLASS_PREFIX = 'theme-';

interface ColorSchemeContextValue {
  colorScheme: ColorScheme;
  setColorScheme: (scheme: ColorScheme) => void;
  /** The saved custom colour (`#rrggbb`), kept while a preset is active. */
  customColor: string | null;
  /** Saves the colour and switches to the custom scheme. */
  setCustomColor: (hex: string) => void;
}

const ColorSchemeContext = createContext<ColorSchemeContextValue>({
  colorScheme: 'blue',
  setColorScheme: () => {},
  customColor: null,
  setCustomColor: () => {},
});

function getStoredCustomColor(): string | null {
  if (typeof window === 'undefined') return null;
  return parseHex(localStorage.getItem(CUSTOM_STORAGE_KEY) ?? '');
}

function getInitialScheme(): ColorScheme {
  if (typeof window === 'undefined') return 'blue';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'custom' && getStoredCustomColor()) return 'custom';
  if (stored && COLOR_SCHEMES.includes(stored as PresetScheme)) return stored as PresetScheme;
  return 'blue';
}

function applyScheme(scheme: ColorScheme, customColor: string | null) {
  const root = document.documentElement;
  COLOR_SCHEMES.forEach((s) => root.classList.remove(`${CLASS_PREFIX}${s}`));
  root.classList.remove('dark', 'dark-mode');
  const vars = customSchemeVars(customColor ?? '#000000');
  if (scheme === 'custom' && customColor) {
    Object.entries(vars).forEach(([name, value]) => root.style.setProperty(name, value));
  } else {
    Object.keys(vars).forEach((name) => root.style.removeProperty(name));
    root.classList.add(`${CLASS_PREFIX}${scheme}`);
  }
  root.setAttribute('data-theme', 'light');
}

export function ColorSchemeProvider({ children }: { children: React.ReactNode }) {
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(getInitialScheme);
  const [customColor, setCustomColorState] = useState<string | null>(getStoredCustomColor);

  useEffect(() => {
    applyScheme(colorScheme, customColor);
  }, [colorScheme, customColor]);

  const setColorScheme = useCallback((scheme: ColorScheme) => {
    setColorSchemeState(scheme);
    localStorage.setItem(STORAGE_KEY, scheme);
  }, []);

  const setCustomColor = useCallback((hex: string) => {
    const color = parseHex(hex);
    if (!color) return;
    setCustomColorState(color);
    setColorSchemeState('custom');
    localStorage.setItem(CUSTOM_STORAGE_KEY, color);
    localStorage.setItem(STORAGE_KEY, 'custom');
  }, []);

  return (
    <ColorSchemeContext.Provider value={{ colorScheme, setColorScheme, customColor, setCustomColor }}>
      {children}
    </ColorSchemeContext.Provider>
  );
}

export function useColorScheme() {
  return useContext(ColorSchemeContext);
}

/**
 * Returns Tailwind class names for the current color scheme.
 * Use this instead of hardcoding `bg-blue-600` etc.
 */
export function useSchemeClasses() {
  const { colorScheme } = useColorScheme();
  return SCHEME_CLASSES[colorScheme];
}

const SCHEME_CLASSES: Record<ColorScheme, {
  bg: string;
  bgHover: string;
  bgLight: string;
  bgSelected: string;
  text: string;
  textHover: string;
  ring: string;
  border: string;
}> = {
  blue: {
    bg: 'bg-blue-600', bgHover: 'hover:bg-blue-700', bgLight: 'bg-blue-50', bgSelected: 'bg-blue-100',
    text: 'text-blue-600', textHover: 'hover:text-blue-800', ring: 'ring-blue-500', border: 'border-blue-600',
  },
  green: {
    bg: 'bg-green-600', bgHover: 'hover:bg-green-700', bgLight: 'bg-green-50', bgSelected: 'bg-green-100',
    text: 'text-green-600', textHover: 'hover:text-green-800', ring: 'ring-green-500', border: 'border-green-600',
  },
  purple: {
    bg: 'bg-purple-600', bgHover: 'hover:bg-purple-700', bgLight: 'bg-purple-50', bgSelected: 'bg-purple-100',
    text: 'text-purple-600', textHover: 'hover:text-purple-800', ring: 'ring-purple-500', border: 'border-purple-600',
  },
  rose: {
    bg: 'bg-rose-600', bgHover: 'hover:bg-rose-700', bgLight: 'bg-rose-50', bgSelected: 'bg-rose-100',
    text: 'text-rose-600', textHover: 'hover:text-rose-800', ring: 'ring-rose-500', border: 'border-rose-600',
  },
  plain: {
    bg: 'bg-gray-600', bgHover: 'hover:bg-gray-700', bgLight: 'bg-gray-50', bgSelected: 'bg-gray-100',
    text: 'text-gray-600', textHover: 'hover:text-gray-800', ring: 'ring-gray-500', border: 'border-gray-600',
  },
  // The custom colour has no Tailwind palette: read the variables applyScheme sets.
  custom: {
    bg: 'bg-[var(--theme-primary)]', bgHover: 'hover:bg-[var(--theme-button-primary-hover)]',
    bgLight: 'bg-[var(--theme-table-row-hover-bg)]', bgSelected: 'bg-[var(--theme-table-row-selected-bg)]',
    text: 'text-[var(--theme-primary)]', textHover: 'hover:text-[var(--theme-text-link-hover)]',
    ring: 'ring-[var(--theme-primary)]', border: 'border-[var(--theme-primary)]',
  },
};

const SCHEME_COLORS: Record<PresetScheme, string> = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  purple: 'bg-purple-500',
  rose: 'bg-rose-500',
  plain: 'bg-gray-500',
};

const MULTICOLOUR =
  'conic-gradient(#ef4444, #f59e0b, #eab308, #22c55e, #06b6d4, #3b82f6, #a855f7, #ec4899, #ef4444)';

export function ColorSchemePicker({ className = '' }: { className?: string }) {
  const { colorScheme, setColorScheme, customColor, setCustomColor } = useColorScheme();
  const isCustom = colorScheme === 'custom';

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {COLOR_SCHEMES.map((scheme) => (
        <button
          key={scheme}
          onClick={() => setColorScheme(scheme)}
          className={`w-5 h-5 rounded-full transition-all ${SCHEME_COLORS[scheme]} ${
            colorScheme === scheme
              ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
              : 'hover:scale-110 opacity-70 hover:opacity-100'
          }`}
          title={scheme.charAt(0).toUpperCase() + scheme.slice(1)}
          aria-label={`${scheme} color scheme`}
        />
      ))}
      <ColorPickerPopover
        value={customColor}
        onChange={setCustomColor}
        onOpenChange={(open) => {
          // Clicking the circle picks it, like the presets: reapply the saved colour.
          if (open && customColor && !isCustom) setCustomColor(customColor);
        }}
      >
        <button
          type="button"
          className={`w-5 h-5 rounded-full transition-all ${
            isCustom ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-110 opacity-70 hover:opacity-100'
          }`}
          style={{ background: customColor ?? MULTICOLOUR }}
          title="Custom colour"
          aria-label="Custom colour"
        />
      </ColorPickerPopover>
    </div>
  );
}
